/**
 * Generic DataTable Component
 *
 * Configuration-driven table with support for:
 * - Row selection (single/multi)
 * - Sorting (client-side)
 * - Row actions dropdown
 * - Custom cell rendering
 * - Loading/empty states
 * - Pagination
 *
 * Reduced from 700+ lines of duplicated table code across the codebase.
 */

import React from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  SortingState,
  Row,
} from '@tanstack/react-table';
import { MoreHorizontal, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type {
  DataTableConfig,
  RowAction,
  SelectionConfig,
} from '@/types/datatable';

interface DataTableProps<TData, TValue> extends Partial<DataTableConfig<TData>> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
}

export function DataTable<TData, TValue>({
  columns,
  data,
  selection,
  actions,
  emptyState,
  loading,
  sorting: sortingConfig,
  pagination: paginationConfig,
  className,
  bordered = true,
  striped = false,
  getRowClassName,
  onRowClick,
}: DataTableProps<TData, TValue>) {
  // Internal sorting state (if not controlled externally)
  const [internalSorting, setInternalSorting] = React.useState<SortingState>([]);

  // Use external sorting if provided, otherwise use internal
  const sorting = sortingConfig?.sorting ?? internalSorting;
  const onSortingChange = sortingConfig?.onSortingChange ?? setInternalSorting;

  // Build columns with selection and actions
  const enhancedColumns = React.useMemo(() => {
    const cols: ColumnDef<TData, unknown>[] = [];

    // Add selection column if configured
    if (selection && selection.type !== 'none') {
      cols.push({
        id: 'select',
        header: ({ table }) => {
          if (selection.type === 'multi' && selection.showSelectAll !== false) {
            return (
              <Checkbox
                checked={
                  table.getIsAllPageRowsSelected() ||
                  (table.getIsSomePageRowsSelected() && 'indeterminate')
                }
                onCheckedChange={(value) => {
                  const checked = value === true;
                  table.toggleAllPageRowsSelected(checked);

                  // Update selection state
                  const newSelection = new Set(selection.selectedIds);
                  table.getRowModel().rows.forEach((row) => {
                    const id = selection.getRowId(row.original);
                    if (checked) {
                      newSelection.add(id);
                    } else {
                      newSelection.delete(id);
                    }
                  });
                  selection.onSelectionChange(newSelection);
                }}
                aria-label="Select all"
              />
            );
          }
          return null;
        },
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => {
              const checked = value === true;
              row.toggleSelected(checked);

              // Update selection state
              const id = selection.getRowId(row.original);
              const newSelection = new Set(selection.selectedIds);
              if (checked) {
                if (selection.type === 'single') {
                  newSelection.clear();
                }
                newSelection.add(id);
              } else {
                newSelection.delete(id);
              }
              selection.onSelectionChange(newSelection);
            }}
            aria-label="Select row"
          />
        ),
        enableSorting: false,
        size: 40,
      });
    }

    // Add data columns
    cols.push(...(columns as ColumnDef<TData, unknown>[]));

    // Add actions column if configured
    if (actions && actions.length > 0) {
      cols.push({
        id: 'actions',
        cell: ({ row }) => <RowActionsCell row={row} actions={actions} />,
        enableSorting: false,
        size: 50,
      });
    }

    return cols;
  }, [columns, selection, actions]);

  // Setup row selection state
  const rowSelection = React.useMemo(() => {
    if (!selection) return {};

    const selectionState: Record<string, boolean> = {};
    data.forEach((row, index) => {
      const id = selection.getRowId(row);
      selectionState[String(index)] = selection.selectedIds.has(id);
    });
    return selectionState;
  }, [selection, data]);

  const table = useReactTable({
    data,
    columns: enhancedColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: paginationConfig ? getPaginationRowModel() : undefined,
    getSortedRowModel: sortingConfig?.enableSorting !== false ? getSortedRowModel() : undefined,
    onSortingChange,
    state: {
      sorting,
      rowSelection,
      pagination: paginationConfig
        ? {
            pageIndex: paginationConfig.pageIndex,
            pageSize: paginationConfig.pageSize,
          }
        : undefined,
    },
    enableRowSelection: !!selection,
    enableSorting: sortingConfig?.enableSorting !== false,
    enableMultiSort: sortingConfig?.enableMultiSort ?? false,
    manualPagination: false,
  });

  // Loading state
  if (loading?.isLoading) {
    const skeletonRows = loading.skeletonRows ?? 5;
    if (loading.component) {
      return <>{loading.component}</>;
    }
    return (
      <div className="space-y-2">
        {Array.from({ length: skeletonRows }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      <div className={cn('rounded-md', bordered && 'border')}>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent">
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const isSorted = header.column.getIsSorted();

                  return (
                    <TableHead
                      key={header.id}
                      style={{ width: header.getSize() }}
                      className={cn(
                        canSort && 'cursor-pointer select-none',
                        header.id === 'select' && 'w-12',
                        header.id === 'actions' && 'w-12'
                      )}
                      onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                    >
                      {header.isPlaceholder ? null : (
                        <div className="flex items-center gap-2">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {canSort && (
                            <span className="ml-auto">
                              {isSorted === 'asc' ? (
                                <ArrowUp className="h-4 w-4" />
                              ) : isSorted === 'desc' ? (
                                <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 opacity-50" />
                              )}
                            </span>
                          )}
                        </div>
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className={cn(
                    'group hover:bg-muted/50 transition-colors',
                    striped && 'even:bg-muted/20',
                    onRowClick && 'cursor-pointer',
                    getRowClassName?.(row.original)
                  )}
                  onClick={() => onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        cell.column.id === 'select' && 'w-12',
                        cell.column.id === 'actions' && 'w-12'
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={enhancedColumns.length}
                  className="h-24 text-center"
                >
                  {emptyState?.component ? (
                    emptyState.component
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      {emptyState?.icon && (
                        <emptyState.icon className="h-8 w-8 opacity-50" />
                      )}
                      <p>{emptyState?.message ?? 'Nenhum resultado encontrado.'}</p>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {paginationConfig && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            {paginationConfig.totalRows !== undefined && (
              <span>
                Total: {paginationConfig.totalRows} resultado
                {paginationConfig.totalRows !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => paginationConfig.onPageChange(paginationConfig.pageIndex - 1)}
              disabled={paginationConfig.pageIndex === 0}
            >
              Anterior
            </Button>
            <div className="text-sm">
              Página {paginationConfig.pageIndex + 1}
              {paginationConfig.totalRows !== undefined &&
                ` de ${Math.ceil(paginationConfig.totalRows / paginationConfig.pageSize)}`}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => paginationConfig.onPageChange(paginationConfig.pageIndex + 1)}
              disabled={
                paginationConfig.totalRows !== undefined &&
                (paginationConfig.pageIndex + 1) * paginationConfig.pageSize >=
                  paginationConfig.totalRows
              }
            >
              Próximo
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Row actions cell component
 */
function RowActionsCell<TData>({
  row,
  actions,
}: {
  row: Row<TData>;
  actions: RowAction<TData>[];
}) {
  // Filter actions based on show condition
  const visibleActions = actions.filter(
    (action) => action.show === undefined || action.show(row.original)
  );

  if (visibleActions.length === 0) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <MoreHorizontal className="h-4 w-4" />
          <span className="sr-only">Abrir menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {visibleActions.map((action, index) => (
          <React.Fragment key={action.key}>
            {action.separatorBefore && index > 0 && <DropdownMenuSeparator />}
            <DropdownMenuItem
              onClick={() => action.onClick(row.original)}
              disabled={action.disabled?.(row.original)}
              className={cn(
                'gap-2',
                action.destructive &&
                  'text-destructive focus:text-destructive'
              )}
            >
              {action.icon && <action.icon className="h-4 w-4" />}
              {action.label}
            </DropdownMenuItem>
          </React.Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
